/**
 * Action cameras split long recordings into ~4 GB "chapters". GoPro names them
 * so the recording number stays fixed while the chapter counts up:
 *   HERO5 and later: GX01_1234.MP4, GX02_1234.MP4 (GH = AVC, GX = HEVC, GL = low-res)
 *   HERO4 and older: GOPR1234.MP4, then GP011234.MP4, GP021234.MP4
 * (written here with "_" only for readability; real names have no separator).
 */
export interface ChapterInfo {
  /** Same for every chapter of one recording. */
  readonly key: string;
  readonly chapter: number;
}

export function parseChapterName(name: string): ChapterInfo | null {
  const modern = /^(G[HX])(\d{2})(\d{4})\.MP4$/i.exec(name);
  if (modern) return { key: `${modern[1]!.toUpperCase()}${modern[3]}`, chapter: Number(modern[2]) };
  const first = /^GOPR(\d{4})\.MP4$/i.exec(name);
  if (first) return { key: `GOPR${first[1]}`, chapter: 0 };
  const later = /^GP(\d{2})(\d{4})\.MP4$/i.exec(name);
  if (later) return { key: `GOPR${later[2]}`, chapter: Number(later[1]) };
  return null;
}

export interface ChapterGrouping<T> {
  /** Recordings split into two or more chapters, each in playback order. */
  readonly recordings: ReadonlyArray<{ readonly key: string; readonly files: readonly T[] }>;
  /** Everything else, in the original order. */
  readonly singles: readonly T[];
}

/** Groups files that are chapters of the same recording. Duplicate chapter numbers are kept apart. */
export function groupChapters<T extends { readonly name: string }>(files: readonly T[]): ChapterGrouping<T> {
  const byKey = new Map<string, Array<{ file: T; chapter: number }>>();
  const singles: T[] = [];
  for (const file of files) {
    const info = parseChapterName(file.name);
    if (!info) {
      singles.push(file);
      continue;
    }
    const list = byKey.get(info.key) ?? [];
    list.push({ file, chapter: info.chapter });
    byKey.set(info.key, list);
  }
  const recordings: Array<{ key: string; files: T[] }> = [];
  for (const [key, list] of byKey) {
    const chapters = new Set(list.map((entry) => entry.chapter));
    if (list.length < 2 || chapters.size !== list.length) {
      singles.push(...list.map((entry) => entry.file));
      continue;
    }
    recordings.push({ key, files: list.sort((a, b) => a.chapter - b.chapter).map((entry) => entry.file) });
  }
  return { recordings, singles };
}
